import { useEffect, useState } from "react";
import { STATUS_OPTIONS, type StatusId } from "#shared/constants.ts";
import type { EntityDetail } from "#shared/schemas/entityDetail.ts";
import { StatusChip } from "@web/components/StatusChip.tsx";
import { formatFollowUp } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";
import type { StatusPatch } from "./api.ts";

const INPUT =
  "h-8 w-full min-w-0 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none disabled:opacity-50";

/**
 * Status, follow-up, Drive — first-class pipeline controls, not custom fields.
 * Follow-up uses the native date picker (`lang=en-GB` → dd/mm/yyyy calendar).
 */
export function StatusSection({
  detail,
  disabled,
  onSave,
  compact,
}: {
  detail: EntityDetail;
  disabled: boolean;
  onSave: (patch: StatusPatch) => void;
  /** Hide Drive when the Overview group already has enough. */
  compact?: boolean;
}) {
  const status = detail.status?.status ?? "none";
  const followUp = detail.status?.follow_up ?? "";
  const [drive, setDrive] = useState(detail.status?.drive_url ?? "");

  useEffect(() => {
    setDrive(detail.status?.drive_url ?? "");
  }, [detail.status?.drive_url]);

  function commitDrive() {
    const next = drive.trim();
    const current = detail.status?.drive_url ?? "";
    if (next === current) return;
    onSave({ drive_url: next === "" ? null : next });
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">Overview</h3>
        <StatusChip status={status} />
      </div>

      <label className="block space-y-1">
        <span className="text-xs text-muted">Status</span>
        <select
          value={status}
          disabled={disabled}
          className={cn(INPUT)}
          onChange={(e) => onSave({ status: e.target.value as StatusId })}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="text-xs text-muted">Follow up</span>
        <input
          type="date"
          lang="en-GB"
          value={followUp}
          disabled={disabled}
          className={cn(INPUT)}
          aria-describedby="follow-up-hint"
          onChange={(e) => onSave({ follow_up: e.target.value === "" ? null : e.target.value })}
        />
        <span id="follow-up-hint" className="block text-xs text-faint">
          {followUp
            ? `Selected: ${formatFollowUp(followUp)}`
            : "Pick a date — shown as dd-mm-yyyy"}
        </span>
      </label>

      {!compact && (
        <>
          <label className="block space-y-1">
            <span className="text-xs text-muted">Drive folder</span>
            <input
              type="url"
              value={drive}
              disabled={disabled}
              placeholder="https://drive.google.com/…"
              className={cn(INPUT)}
              onChange={(e) => setDrive(e.target.value)}
              onBlur={commitDrive}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitDrive();
                if (e.key === "Escape") setDrive(detail.status?.drive_url ?? "");
              }}
            />
          </label>

          {detail.status?.drive_url && (
            <a
              href={detail.status.drive_url}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-xs text-accent hover:underline"
            >
              Open Drive folder
            </a>
          )}
        </>
      )}
    </section>
  );
}
